import { isIosCompanionApp } from "@/lib/native/runtime";
import { keys } from "@/lib/i18n/portal";
import { translateStandalone } from "@/lib/i18n/standalone";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";

/**
 * Backend error `code` → localized copy.
 *
 * `code` is the stable cross-app contract (`db-error-codes.ts`,
 * `order-error-codes.ts` on the backend); `message` is developer-facing English
 * that names schema columns — "A category with this store id and slug already
 * exists" — and must never reach a seller. Error-localization contract v1 §1.
 *
 * A new backend error code needs a key here in the same change, or it silently
 * degrades to the generic message.
 */
const ERROR_CODE_MESSAGE_KEYS: Record<string, string> = {
  DUPLICATE_RECORD: keys.errors.duplicateRecord,
  INVALID_REFERENCE: keys.errors.invalidReference,
  RECORD_NOT_FOUND: keys.errors.notFound,
  INVALID_VALUE: keys.errors.invalidValue,
  STALE_STATUS: keys.errors.orderStaleStatus,
  // Client-assigned (useUpdateOrderStatusMutation): a status change another
  // device already made. The order is refetched, so no "refresh" instruction.
  ORDER_UPDATED_ELSEWHERE: keys.coreOrders.updatedElsewhere,
  VARIANT_DRIFT: keys.errors.orderVariantDrift,
  SHIPPING_NOT_PRICED: keys.errors.orderShippingNotPriced,
  SHIPPING_ALREADY_PRICED: keys.errors.orderShippingAlreadyPriced,
  WALLET_BALANCE_INSUFFICIENT: keys.errors.orderWalletBalanceInsufficient,
  ORDER_LOCKED_UNSETTLED: keys.errors.orderLockedUnsettled,
  SELLER_SUBSCRIPTION_RESTRICTED: keys.seller.subscriptionRestrictedToast,
  // The member's ROLE lacks the permission (403 from SellerAuthGuard). Not a
  // sign-in problem: signing in again would change nothing; the owner can.
  SELLER_PERMISSION_DENIED: keys.errors.permissionDenied,
  // Free's one order-moderator seat is held already (D2, 409).
  TEAM_SEAT_TAKEN: keys.coreSettings.teamSeatTaken,
  // A seller owns the maximum number of stores (Q8, 409 on POST /seller/store).
  STORE_LIMIT_REACHED: keys.auth.createStoreLimitReached,
  // A chosen store link that is reserved or already a store's (R2-D5).
  STORE_SLUG_TAKEN: keys.auth.createStoreSlugTaken,
  // Bulk catalogue import/export. These are FILE-level problems — a row-level
  // problem never reaches here, it is rendered in the preview by
  // `importIssueCopy`.
  IMPORT_FILE_EMPTY: keys.errors.importFileEmpty,
  IMPORT_FILE_UNREADABLE: keys.errors.importFileUnreadable,
  IMPORT_FILE_ENCODING: keys.errors.importFileEncoding,
  IMPORT_HEADER_UNRECOGNIZED: keys.errors.importHeaderUnrecognized,
  IMPORT_TOO_MANY_ROWS: keys.errors.importTooManyRows,
  IMPORT_FILE_TOO_LARGE: keys.errors.importFileTooLarge,
  IMPORT_JOB_NOT_PENDING: keys.errors.importJobNotPending,
  PRODUCT_EXPORT_TOO_LARGE: keys.errors.productExportTooLarge,
};

/**
 * What to show a seller when something failed. Never the raw thrown message.
 *
 * Resolves from `code`, then 401/403/404, else the generic localized sentence.
 * Only an uncoded 401/403 reads "sign in to continue"; a coded 403 (a missing
 * role permission, a paid feature) has its own sentence above.
 * Resolves the locale itself (from `<html lang>`), so it works in event
 * handlers without threading `t` through. Carrier-specific codes go through
 * `resolveCarrierErrorMessage` instead — they are not in this map.
 */
export function apiErrorMessage(error: unknown): string {
  if (error instanceof ApiJsonClientError) {
    if (isIosCompanionApp()) {
      if (error.code === "TEAM_SEAT_TAKEN") return translateStandalone(keys.iosCompanion.seatTaken);
      if (["SELLER_SUBSCRIPTION_RESTRICTED", "WALLET_BALANCE_INSUFFICIENT", "ORDER_LOCKED_UNSETTLED"].includes(error.code ?? "")) {
        return translateStandalone(keys.iosCompanion.restricted);
      }
    }
    const byCode = error.code ? ERROR_CODE_MESSAGE_KEYS[error.code] : undefined;
    if (byCode) return translateStandalone(byCode);
    if (error.status === 401 || error.status === 403) {
      return translateStandalone(keys.errors.unauthorized);
    }
    if (error.status === 404) return translateStandalone(keys.errors.notFound);
  }
  return translateStandalone(keys.errors.generic);
}
